import { supabase } from "@/lib/supabase";
import { isUuid } from "@/lib/uuid";
import type { Shipment, ShipmentItem, ReceivingRecord, JobAllocation, ShipmentStatus, ReceivingStatus, AllocationStatus } from "@/data/WarehouseInventoryContext";
import { rebuildJobMaterialsList, syncJobMaterialState, syncProductRegistryStock, syncStockLevels, syncWarehouseStock } from "@/services/inventory.service";
import { REALTIME_ROW_PATCHING_ENABLED, SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { writeReallocationAuditEntry } from "@/services/job-operations.service";
import { SystemUpdateService } from '@/services/system-update.service';
import { LoadoutSyncService } from "@/services/loadoutSync.service";

const ENABLE_METADATA_MATERIALS_FALLBACK = (import.meta.env.VITE_ENABLE_METADATA_MATERIALS_FALLBACK ?? "true") === "true";
const WAREHOUSE_SERVICE_CACHE_TTL_MS = 1_000;

const warehouseServiceCache = new Map<string, { at: number; value: unknown }>();
const warehouseServiceInFlight = new Map<string, Promise<unknown>>();
const warehouseAllocationOperationInFlight = new Map<string, Promise<unknown>>();

/**
 * Computes the correct material_readiness for a job by aggregating its
 * receiving_records and job_allocations, then writes it back to jobs.
 *
 * This is the authoritative sync path when job_materials rows don't exist.
 */
async function syncJobMaterialReadiness(jobId: string): Promise<void> {
  try {
    const [records, allocations, jobRowResult, jobMaterialsRows] = await Promise.all([
      fetchReceivingRowsForJob(jobId),
      fetchJobAllocationRowsForJob(jobId),
      supabase.from("jobs").select("metadata").eq("id", jobId).maybeSingle(),
      fetchJobMaterialsRowsForJob(jobId),
    ]);

    if (jobRowResult.error) {
      console.error("[WarehouseService] Failed to load job metadata during readiness sync:", jobRowResult.error.message);
    }

    const currentMetadata = ((jobRowResult.data as { metadata?: Record<string, unknown> | null } | null)?.metadata ?? {}) as Record<string, unknown>;
    const currentMaterialRows = ENABLE_METADATA_MATERIALS_FALLBACK && Array.isArray(currentMetadata.materialsList)
      ? (currentMetadata.materialsList as Array<Record<string, unknown>>)
      : [];

    // Build required quantities by SKU from job_materials rows (authoritative DB source),
    // then supplement with metadata.materialsList for any SKUs not already covered.
    // This covers ALL materials the job needs — including those not yet ordered
    // in partial-ordering scenarios (where only a subset was ordered first).
    const requiredBySku = new Map<string, number>();
    // Primary: job_materials rows (queried by UUID — authoritative)
    for (const row of jobMaterialsRows) {
      const sku = String(row.sku || "").trim();
      if (!sku) continue;
      const required = Number(row.quantity_required ?? 0);
      if (required > 0) {
        requiredBySku.set(sku, (requiredBySku.get(sku) ?? 0) + required);
      }
    }
    // Fallback: metadata.materialsList for any SKUs not captured in job_materials
    for (const rawRow of currentMaterialRows) {
      const sku = String(rawRow.sku || "").trim();
      if (!sku || requiredBySku.has(sku)) continue;
      const required = Number(rawRow.quantityRequired ?? rawRow.quantity_required ?? 0);
      if (required > 0) {
        requiredBySku.set(sku, required);
      }
    }

    // Primary check: allocation-based readiness.
    // If all required quantities are covered by "Ready for Install" allocations,
    // the job is install-ready regardless of receiving records state.
    const readyAllocatedBySku = new Map<string, number>();
    for (const alloc of allocations) {
      if (String(alloc.status ?? "").trim() === "Ready for Install") {
        const sku = String(alloc.sku ?? "").trim();
        if (sku) {
          readyAllocatedBySku.set(sku, (readyAllocatedBySku.get(sku) ?? 0) + Math.max(0, Number(alloc.quantity ?? 0)));
        }
      }
    }
    const allRequiredCoveredByReadyAllocations = requiredBySku.size > 0 &&
      Array.from(requiredBySku.entries()).every(
        ([sku, required]) => (readyAllocatedBySku.get(sku) ?? 0) >= required
      );

    // Aggregate received/expected by SKU from receiving_records.
    const totalsBySku = new Map<string, { expected: number; received: number }>();
    records.forEach((row) => {
      const sku = String((row as { sku?: unknown }).sku || "").trim();
      if (!sku) return;
      const expected = Number((row as { expected_quantity?: unknown }).expected_quantity ?? 0);
      const received = Number((row as { received_quantity?: unknown }).received_quantity ?? 0);
      const prev = totalsBySku.get(sku) || { expected: 0, received: 0 };
      totalsBySku.set(sku, {
        expected: prev.expected + Math.max(0, expected),
        received: prev.received + Math.max(0, received),
      });
    });

    let materialReadiness: string;

    const allAllocationsReady =
      allocations.length > 0 && allocations.every((a) => a.status === "Ready for Install");

    if (allRequiredCoveredByReadyAllocations) {
      // All required quantities are allocated and marked "Ready for Install" —
      // job is install-ready. Receiving records state is secondary.
      materialReadiness = "Staged for Job";
    } else {
      const total = records.length;
      if (total === 0) {
        if (!allAllocationsReady) return; // Nothing to sync yet
        materialReadiness = "Staged for Job";
      } else {
        const fullyReceivedCount = records.filter((r) => {
          if (r.status === "Fully Received") return true;
          const expected = Number(r.expected_quantity ?? 0);
          const received = Number(r.received_quantity ?? 0);
          return expected > 0 && received >= expected;
        }).length;

        const anyReceived = records.some((r) => Number(r.received_quantity ?? 0) > 0);

        // All existing receiving records are individually fully received.
        const allReceivingRecordsFullyReceived = fullyReceivedCount === total;

        // Additionally: if the metadata has a required materials list, verify that
        // ALL required SKUs are covered — not just the ones that were partially
        // ordered. This prevents a partial order of N/M materials from marking the
        // job as "Fully Received" once those N items are delivered while M-N items
        // remain unordered.
        const allFullyReceived = allReceivingRecordsFullyReceived && (
          requiredBySku.size === 0 ||
          Array.from(requiredBySku.entries()).every(
            ([sku, required]) => (totalsBySku.get(sku)?.received ?? 0) >= required
          )
        );

        if (allFullyReceived && allAllocationsReady) {
          materialReadiness = "Staged for Job";
        } else if (allFullyReceived) {
          materialReadiness = "Fully Received";
        } else if (fullyReceivedCount > 0 || anyReceived) {
          materialReadiness = "Partially Received";
        } else {
          materialReadiness = "Ordered";
        }
      }
    }

    // Reuse the same required-vs-received logic for the metadata update pass.
    const allReceivingRecordsFullyReceived = records.length > 0 && records.every((row) => {
      if (row.status === "Fully Received") return true;
      const expected = Number((row as { expected_quantity?: unknown }).expected_quantity ?? 0);
      const received = Number((row as { received_quantity?: unknown }).received_quantity ?? 0);
      return expected > 0 && received >= expected;
    });
    const allFullyReceived = allReceivingRecordsFullyReceived && (
      requiredBySku.size === 0 ||
      Array.from(requiredBySku.entries()).every(
        ([sku, required]) => (totalsBySku.get(sku)?.received ?? 0) >= required
      )
    );

    const nextMaterialRows = currentMaterialRows.map((rawRow) => {
      const row = { ...rawRow };
      if (allFullyReceived || materialReadiness === "Staged for Job") {
        row.missingItems = 0;
        return row;
      }

      const sku = String(row.sku || "").trim();
      if (!sku) return row;
      const totals = totalsBySku.get(sku);
      if (!totals || totals.expected <= 0) {
        // No receiving records for this SKU — if it's in the required list it
        // is entirely unordered/undelivered, so all required quantity is missing.
        const required = requiredBySku.get(sku) ?? 0;
        if (required > 0) row.missingItems = required;
        return row;
      }
      row.missingItems = Math.max(0, totals.expected - totals.received);
      return row;
    });

    const nextMissingItemsList = (allFullyReceived || materialReadiness === "Staged for Job")
      ? ""
      : nextMaterialRows
          .filter((row) => Number(row.missingItems || 0) > 0)
          .map((row) => String(row.itemName || row.sku || "").trim())
          .filter(Boolean)
          .join(", ");

    const nextReadinessNotes = (allFullyReceived || materialReadiness === "Staged for Job")
      ? ""
      : String(currentMetadata.materialReadinessNotes || "");

    const nextMetadata: Record<string, unknown> = {
      ...currentMetadata,
      materialReadiness: materialReadiness,
      missingItemsList: nextMissingItemsList,
      materialReadinessNotes: nextReadinessNotes,
      materialsSummary: {
        source: "metadata_fallback",
        itemCount: nextMaterialRows.length,
        missingCount: nextMaterialRows.filter((row) => Number(row.missingItems || 0) > 0).length,
        hash: `ml_${nextMaterialRows.length}_${nextMaterialRows.reduce((acc, row) => acc + Number(row.missingItems || 0), 0)}`,
        updatedAt: new Date().toISOString(),
      },
    };

    // Advance planningStatus in metadata when materials are fully received so the
    // job exits the ordering queue and becomes visible in scheduling/dispatch.
    // planning_status is NOT a direct column on jobs — it lives in metadata.
    if (materialReadiness === "Staged for Job") {
      nextMetadata.planningStatus = "Staged";
    } else if (materialReadiness === "Fully Received") {
      nextMetadata.planningStatus = "Ready for Install";
    }

    const { error } = await supabase
      .from("jobs")
      .update({
        material_readiness: materialReadiness,
        metadata: nextMetadata,
      })
      .eq("id", jobId);

    if (error) {
      console.error("[WarehouseService] Failed to sync job material_readiness:", error.message);
    } else if (materialReadiness === "Staged for Job") {
      // Fire-and-forget: notify the Portal that a loadout manifest is available.
      void LoadoutSyncService.triggerLoadoutPush(jobId, "loadout.created");
    }
  } catch (err) {
    console.error("[WarehouseService] Exception in syncJobMaterialReadiness:", err);
  }
}

interface DbShipment {
  id: string;
  po_id: string;
  po_number: string;
  external_po_number?: string | null;
  vendor_id: string;
  vendor_name: string;
  items: ShipmentItem[];
  status: string;
  created_at: string;
  expected_delivery: string | null;
  delivered_at: string | null;
  received_at: string | null;
}

interface DbReceivingRecord {
  id: string;
  po_id: string;
  po_number: string;
  vendor_name: string;
  job_id: string;
  job_label: string;
  sku: string;
  product_name: string;
  expected_quantity: number;
  received_quantity: number;
  status: string;
  received_at: string | null;
  received_by: string | null;
  shipment_id: string | null;
  discrepancy_type: string | null;
  discrepancy_notes: string | null;
}

interface DbJobAllocation {
  id: string;
  job_id: string;
  job_label: string;
  product_id: string | null;
  sku: string;
  product_name: string;
  quantity: number;
  status: string;
  allocated_at: string | null;
  allocated_by: string | null;
}

type RealtimePatchPayload<T> = {
  eventType?: string;
  new?: T;
  old?: Partial<T>;
};

function asRealtimePatchPayload<T>(payload: unknown): RealtimePatchPayload<T> {
  const row = (payload ?? {}) as { eventType?: unknown; new?: unknown; old?: unknown };
  return {
    eventType: typeof row.eventType === "string" ? row.eventType : undefined,
    new: row.new && typeof row.new === "object" ? (row.new as T) : undefined,
    old: row.old && typeof row.old === "object" ? (row.old as Partial<T>) : undefined,
  };
}

let productIdColumnSupport: boolean | null = null;

async function withWarehouseServiceCache<T>(key: string, run: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const cached = warehouseServiceCache.get(key);
  if (cached && now - cached.at < WAREHOUSE_SERVICE_CACHE_TTL_MS) {
    return cached.value as T;
  }

  const inFlight = warehouseServiceInFlight.get(key);
  if (inFlight) {
    return inFlight as Promise<T>;
  }

  const promise = run().then((result) => {
    warehouseServiceCache.set(key, { at: Date.now(), value: result });
    return result;
  });

  warehouseServiceInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    warehouseServiceInFlight.delete(key);
  }
}

function getJobWarehouseCacheKey(kind: string, jobId: string): string {
  return `${kind}:${String(jobId || "").trim()}`;
}

function invalidateJobWarehouseCache(jobId: string): void {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return;
  [
    getJobWarehouseCacheKey("job_materials", normalizedJobId),
    getJobWarehouseCacheKey("job_allocations", normalizedJobId),
    getJobWarehouseCacheKey("receiving_records", normalizedJobId),
  ].forEach((key) => {
    warehouseServiceCache.delete(key);
    warehouseServiceInFlight.delete(key);
  });
}

async function withWarehouseAllocationOperation<T>(key: string, run: () => Promise<T>): Promise<T> {
  const inFlight = warehouseAllocationOperationInFlight.get(key);
  if (inFlight) {
    return inFlight as Promise<T>;
  }

  const promise = run();
  warehouseAllocationOperationInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (warehouseAllocationOperationInFlight.get(key) === promise) {
      warehouseAllocationOperationInFlight.delete(key);
    }
  }
}

async function supportsProductIdColumns(): Promise<boolean> {
  if (productIdColumnSupport !== null) return productIdColumnSupport;
  const [jobMaterialsProbe, allocationsProbe] = await Promise.all([
    supabase.from("job_materials").select("product_id").limit(1),
    supabase.from("job_allocations").select("product_id").limit(1),
  ]);
  const missing =
    jobMaterialsProbe.error?.code === "42703" ||
    allocationsProbe.error?.code === "42703";
  productIdColumnSupport = !missing;
  return productIdColumnSupport;
}

async function fetchJobMaterialsRowsForJob(jobId: string): Promise<Array<{ id?: string | null; sku?: string | null; item_name?: string | null; quantity_required?: number | null; quantity_allocated?: number | null; product_id?: string | null }>> {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return [];
  const canUseProductId = await supportsProductIdColumns();
  return withWarehouseServiceCache(getJobWarehouseCacheKey("job_materials", normalizedJobId), async () => {
    const { data, error } = await supabase
      .from("job_materials")
      .select(canUseProductId ? "id, sku, item_name, quantity_required, quantity_allocated, product_id" : "id, sku, item_name, quantity_required, quantity_allocated")
      .eq("job_id", normalizedJobId);
    if (error) {
      console.error("[WarehouseService] Error loading job_materials rows:", error.message);
      return [];
    }
    return data ?? [];
  });
}

async function fetchJobAllocationRowsForJob(jobId: string): Promise<DbJobAllocation[]> {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return [];
  return withWarehouseServiceCache(getJobWarehouseCacheKey("job_allocations", normalizedJobId), async () => {
    const { data, error } = await supabase
      .from("job_allocations")
      .select("*")
      .eq("job_id", normalizedJobId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("[WarehouseService] Error loading job_allocations rows:", error.message);
      return [];
    }
    return (data ?? []) as DbJobAllocation[];
  });
}

async function fetchReceivingRowsForJob(jobId: string): Promise<DbReceivingRecord[]> {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return [];
  return withWarehouseServiceCache(getJobWarehouseCacheKey("receiving_records", normalizedJobId), async () => {
    const { data, error } = await supabase
      .from("receiving_records")
      .select("id, po_id, po_number, vendor_name, job_id, job_label, sku, product_name, expected_quantity, received_quantity, status, received_at, received_by, shipment_id, discrepancy_type, discrepancy_notes")
      .eq("job_id", normalizedJobId);
    if (error) {
      console.error("[WarehouseService] Error loading receiving_records rows:", error.message);
      return [];
    }
    return (data ?? []) as DbReceivingRecord[];
  });
}

async function resolveProductIdForJobSku(jobId: string, sku: string): Promise<string | null> {
  if (!(await supportsProductIdColumns())) return null;
  const normalizedJobId = String(jobId || "").trim();
  const normalizedSku = String(sku || "").trim();
  if (!normalizedJobId || !normalizedSku) return null;
  const rows = await fetchJobMaterialsRowsForJob(normalizedJobId);
  const match = rows.find((row) => String(row.sku ?? "").trim() === normalizedSku);
  return match?.product_id ?? null;
}

async function resolveRequiredMaterialFromJobMetadata(
  jobId: string,
  sku: string,
): Promise<{ quantity: number; itemName: string }> {
  if (!ENABLE_METADATA_MATERIALS_FALLBACK) {
    const normalizedSku = String(sku || "").trim();
    return { quantity: 0, itemName: normalizedSku };
  }
  const normalizedJobId = String(jobId || "").trim();
  const normalizedSku = String(sku || "").trim();
  if (!normalizedJobId || !normalizedSku) return { quantity: 0, itemName: normalizedSku };
  const normalizeAllocSku = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
  const skuKey = normalizeAllocSku(normalizedSku);
  const { data } = await supabase
    .from("jobs")
    .select("metadata")
    .eq("id", normalizedJobId)
    .maybeSingle();
  const metadata = (data?.metadata && typeof data.metadata === "object")
    ? (data.metadata as Record<string, unknown>)
    : {};
  const rows = Array.isArray(metadata.materialsList) ? metadata.materialsList : [];
  let quantity = 0;
  let itemName = normalizedSku;
  rows.forEach((raw) => {
    if (!raw || typeof raw !== "object") return;
    const row = raw as Record<string, unknown>;
    const rowSkuKey = normalizeAllocSku(String(row.sku ?? ""));
    if (!rowSkuKey || rowSkuKey !== skuKey) return;
    quantity += Math.max(0, Number(row.quantityRequired ?? 0));
    const candidateName = String(row.itemName ?? row.item_name ?? "").trim();
    if (candidateName) itemName = candidateName;
  });
  return { quantity, itemName };
}

async function resolveRequiredQtyForJobSku(
  jobId: string,
  sku: string,
  productId?: string | null,
): Promise<{ quantity: number; source: "job_materials" | "metadata"; itemName: string }> {
  const normalizedJobId = String(jobId || "").trim();
  const normalizedSku = String(sku || "").trim();
  if (!normalizedJobId || !normalizedSku) return { quantity: 0, source: "metadata", itemName: normalizedSku };
  const canUseProductId = await supportsProductIdColumns();
  const normalizedTargetSku = normalizedSku.toUpperCase().replace(/[^A-Z0-9]+/g, "");
  const safeProductId = String(productId ?? "").trim();
  const canFilterByProductId = canUseProductId && isUuid(safeProductId);
  const data = await fetchJobMaterialsRowsForJob(normalizedJobId);

  const fromMaterials = (data ?? []).reduce((sum, row) => {
    if (canFilterByProductId && String(row.product_id ?? "").trim() !== safeProductId) return sum;
    const rowSkuKey = String(row.sku ?? "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
    if (!canFilterByProductId && rowSkuKey && rowSkuKey !== normalizedTargetSku) return sum;
    return sum + Math.max(0, Number(row.quantity_required ?? 0));
  }, 0);

  if (fromMaterials > 0) {
    return { quantity: fromMaterials, source: "job_materials", itemName: normalizedSku };
  }
  const metadata = await resolveRequiredMaterialFromJobMetadata(normalizedJobId, normalizedSku);
  return { quantity: metadata.quantity, source: "metadata", itemName: metadata.itemName };
}

async function ensureJobMaterialRequirementRow(
  jobId: string,
  sku: string,
  quantityRequired: number,
  itemName: string,
): Promise<void> {
  const normalizedJobId = String(jobId || "").trim();
  const normalizedSku = String(sku || "").trim();
  if (!normalizedJobId || !normalizedSku || quantityRequired <= 0) return;
  const existingRows = await fetchJobMaterialsRowsForJob(normalizedJobId);
  const current = existingRows.find((row) => String(row.sku ?? "").trim() === normalizedSku);
  if (current?.id) {
    const prevQty = Math.max(0, Number(current.quantity_required ?? 0));
    if (prevQty >= quantityRequired) return;
    await supabase
      .from("job_materials")
      .update({ quantity_required: quantityRequired })
      .eq("id", current.id);
    return;
  }
  const organizationId = await getCurrentOrganizationId();
  await supabase
    .from("job_materials")
    .insert({
      job_id: normalizedJobId,
      sku: normalizedSku,
      item_name: itemName || normalizedSku,
      quantity_required: quantityRequired,
      quantity_allocated: 0,
      quantity_staged: 0,
      quantity_installed: 0,
      field_purchase: false,
      inventory_assignment: "Stock",
      lifecycle_stage: "Ordered",
      inventory_status: "In Stock",
      organization_id: organizationId,
    });
}

function mapShipmentStatus(status: string): ShipmentStatus {
  const validStatuses: ShipmentStatus[] = ["Ordered", "In Transit", "Delivered", "Received"];
  return validStatuses.includes(status as ShipmentStatus) ? status as ShipmentStatus : "Ordered";
}

function mapReceivingStatus(status: string): ReceivingStatus {
  const validStatuses: ReceivingStatus[] = ["Pending", "Partially Received", "Fully Received"];
  return validStatuses.includes(status as ReceivingStatus) ? status as ReceivingStatus : "Pending";
}

function mapAllocationStatus(status: string): AllocationStatus {
  const validStatuses: AllocationStatus[] = ["Unallocated", "Reserved", "Ready for Install", "Installed"];
  return validStatuses.includes(status as AllocationStatus) ? status as AllocationStatus : "Unallocated";
}

function mapDbShipment(item: DbShipment): Shipment {
  return {
    id: item.id,
    poId: item.po_id,
    poNumber: item.po_number,
    externalPoNumber: item.external_po_number || undefined,
    vendorId: item.vendor_id,
    vendorName: item.vendor_name,
    items: item.items || [],
    status: mapShipmentStatus(item.status),
    createdAt: item.created_at,
    expectedDelivery: item.expected_delivery || undefined,
    deliveredAt: item.delivered_at || undefined,
    receivedAt: item.received_at || undefined,
  };
}

function mapDbReceivingRecord(item: DbReceivingRecord): ReceivingRecord {
  return {
    id: item.id,
    poId: item.po_id,
    poNumber: item.po_number,
    vendorName: item.vendor_name,
    jobId: item.job_id,
    jobLabel: item.job_label,
    sku: item.sku,
    productName: item.product_name,
    expectedQuantity: item.expected_quantity,
    receivedQuantity: item.received_quantity,
    status: mapReceivingStatus(item.status),
    receivedAt: item.received_at || undefined,
    receivedBy: item.received_by || undefined,
    shipmentId: item.shipment_id || undefined,
    discrepancy: item.discrepancy_type ? {
      type: item.discrepancy_type as ReceivingRecord["discrepancy"]["type"],
      notes: item.discrepancy_notes || "",
    } : undefined,
  };
}

function mapDbJobAllocation(item: DbJobAllocation): JobAllocation {
  return {
    id: item.id,
    jobId: item.job_id,
    jobLabel: item.job_label,
    sku: item.sku,
    productName: item.product_name,
    quantity: item.quantity,
    status: mapAllocationStatus(item.status),
    allocatedAt: item.allocated_at || undefined,
    allocatedBy: item.allocated_by || undefined,
  };
}

function looksLikeJobNumber(value: string | null | undefined): boolean {
  return typeof value === "string" && /^JOB-\d+$/i.test(value.trim());
}

async function resolveJobId(value: string | null | undefined): Promise<string | null> {
  if (!value) return null;
  const raw = value.trim();
  if (!raw) return null;
  if (!looksLikeJobNumber(raw)) return raw;

  const { data, error } = await supabase
    .from("jobs")
    .select("id")
    .eq("job_number", raw)
    .maybeSingle();

  if (error) {
    console.warn("[WarehouseService] Failed to resolve job_number to UUID:", error);
    return raw;
  }

  return data?.id ?? raw;
}

const isWarehouseDebugEnabled = typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);

function logWarehouseRealtime(channel: string, message: string) {
  if (!isWarehouseDebugEnabled) return;
  console.debug(`[WarehouseRealtime:${channel}] ${message}`);
}

function normalizeSku(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");
}

function normalizeName(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const WarehouseService = {
  async reconcileJobAllocationsFromReceiving(jobId: string): Promise<{ created: number; skipped: number }> {
    try {
      const resolvedJobId = await resolveJobId(jobId);
      if (!resolvedJobId) return { created: 0, skipped: 0 };

      const [cachedAllocations, cachedJobMaterials] = await Promise.all([
        fetchJobAllocationRowsForJob(resolvedJobId),
        fetchJobMaterialsRowsForJob(resolvedJobId),
      ]);
      const [jobRes, allocationRes, jobMaterialsRes] = await Promise.all([
        supabase
          .from("jobs")
          .select("job_number, metadata")
          .eq("id", resolvedJobId)
          .maybeSingle(),
        Promise.resolve({ data: cachedAllocations, error: null }),
        Promise.resolve({ data: cachedJobMaterials, error: null }),
      ]);

      if (allocationRes.error) {
        console.warn("[WarehouseService] reconcile allocation fetch failed:", allocationRes.error.message);
        return { created: 0, skipped: 0 };
      }
      if (jobMaterialsRes.error) {
        console.warn("[WarehouseService] reconcile job_materials fetch failed:", jobMaterialsRes.error.message);
        return { created: 0, skipped: 0 };
      }
      const fetchReceivingByUuid = supabase
        .from("receiving_records")
        .select("id, job_id, job_label, sku, product_name, received_quantity, status")
        .eq("job_id", resolvedJobId);

      const fetchDeliveredPoByUuid = supabase
        .from("purchase_orders")
        .select("id, job_id, job_label, status")
        .eq("job_id", resolvedJobId)
        .eq("status", "Delivered");

      const [receivingByUuidRes, deliveredPoByUuidRes] = await Promise.all([
        fetchReceivingByUuid,
        fetchDeliveredPoByUuid,
      ]);

      if (receivingByUuidRes.error) {
        console.warn("[WarehouseService] reconcile receiving fetch (uuid) failed:", receivingByUuidRes.error.message);
        return { created: 0, skipped: 0 };
      }
      if (deliveredPoByUuidRes.error) {
        console.warn("[WarehouseService] reconcile delivered purchase_orders fetch (uuid) failed:", deliveredPoByUuidRes.error.message);
        return { created: 0, skipped: 0 };
      }
      const receivingRows = receivingByUuidRes.data ?? [];

      const allocationRows = allocationRes.data ?? [];
      const jobMaterialRows = jobMaterialsRes.data ?? [];
      const deliveredPos = deliveredPoByUuidRes.data ?? [];

      let deliveredLineItems: Array<{ sku: string | null; product_name: string | null; quantity_ordered: number | null; quantity_received: number | null }> = [];
      if (deliveredPos.length > 0) {
        const poIds = deliveredPos
          .map((po) => String(po.id ?? "").trim())
          .filter((poId) => poId.length > 0);
        if (poIds.length > 0) {
          const deliveredLinesRes = await supabase
            .from("po_line_items")
            .select("sku, product_name, quantity_ordered, quantity_received")
            .in("po_id", poIds);
          if (deliveredLinesRes.error) {
            console.warn("[WarehouseService] reconcile delivered po_line_items fetch failed:", deliveredLinesRes.error.message);
            return { created: 0, skipped: 0 };
          }
          deliveredLineItems = deliveredLinesRes.data ?? [];
        }
      }

      if (receivingRows.length === 0 && deliveredLineItems.length === 0) return { created: 0, skipped: 0 };

      const canonicalSkuByNormalizedSku = new Map<string, string>();
      const canonicalSkuByNormalizedName = new Map<string, string>();
      const requiredByCanonicalSku = new Map<string, number>();
      for (const row of jobMaterialRows) {
        const rawSku = String(row.sku ?? "").trim();
        const normalizedSku = normalizeSku(rawSku);
        const normalizedName = normalizeName(row.item_name);
        if (rawSku && normalizedSku && !canonicalSkuByNormalizedSku.has(normalizedSku)) {
          canonicalSkuByNormalizedSku.set(normalizedSku, rawSku);
        }
        if (rawSku && normalizedName && !canonicalSkuByNormalizedName.has(normalizedName)) {
          canonicalSkuByNormalizedName.set(normalizedName, rawSku);
        }
        if (rawSku) {
          const requiredQty = Math.max(0, Number(row.quantity_required ?? 0));
          requiredByCanonicalSku.set(rawSku, (requiredByCanonicalSku.get(rawSku) ?? 0) + requiredQty);
        }
      }

      // Fallback/augmentation from jobs.metadata.materialsList to handle cases
      // where job_materials is stale or incomplete for delivered reconciliation.
      const metadata = (jobRes.data?.metadata && typeof jobRes.data.metadata === "object")
        ? (jobRes.data.metadata as Record<string, unknown>)
        : {};
      const metadataMaterials = ENABLE_METADATA_MATERIALS_FALLBACK && Array.isArray(metadata.materialsList)
        ? (metadata.materialsList as Array<Record<string, unknown>>)
        : [];
      for (const row of metadataMaterials) {
        const rawSku = String(row.sku ?? "").trim();
        const normalizedSku = normalizeSku(rawSku);
        const normalizedName = normalizeName(row.itemName ?? row.item_name);
        if (rawSku && normalizedSku && !canonicalSkuByNormalizedSku.has(normalizedSku)) {
          canonicalSkuByNormalizedSku.set(normalizedSku, rawSku);
        }
        if (rawSku && normalizedName && !canonicalSkuByNormalizedName.has(normalizedName)) {
          canonicalSkuByNormalizedName.set(normalizedName, rawSku);
        }
        if (rawSku) {
          const requiredQty = Math.max(
            0,
            Number(row.quantityRequired ?? row.quantity_required ?? 0),
          );
          if (!requiredByCanonicalSku.has(rawSku)) {
            requiredByCanonicalSku.set(rawSku, requiredQty);
          } else {
            requiredByCanonicalSku.set(rawSku, Math.max(requiredByCanonicalSku.get(rawSku) ?? 0, requiredQty));
          }
        }
      }

      const receivedByCanonicalSku = new Map<string, { qty: number; name: string; label: string }>();
      const unmatchedDeliveredRows: Array<{ sku: string; productName: string; qty: number }> = [];
      for (const row of receivingRows) {
        const sku = String(row.sku ?? "").trim();
        const receivedQty = Math.max(0, Number(row.received_quantity ?? 0));
        if (receivedQty <= 0) continue;

        const normalizedSku = normalizeSku(sku);
        const normalizedName = normalizeName(row.product_name);
        const canonicalSku =
          (normalizedSku ? canonicalSkuByNormalizedSku.get(normalizedSku) : undefined) ??
          (normalizedName ? canonicalSkuByNormalizedName.get(normalizedName) : undefined) ??
          sku;

        if (!canonicalSku) {
          unmatchedDeliveredRows.push({
            sku,
            productName: String(row.product_name ?? ""),
            qty: receivedQty,
          });
          continue;
        }

        const prev = receivedByCanonicalSku.get(canonicalSku);
        receivedByCanonicalSku.set(canonicalSku, {
          qty: (prev?.qty ?? 0) + receivedQty,
          name: String(row.product_name ?? prev?.name ?? canonicalSku),
          label: String(row.job_label ?? prev?.label ?? ""),
        });
      }

      // Fallback source: delivered PO line items (covers gaps where receiving rows
      // are incomplete/missing but PO is delivered).
      const deliveredByCanonicalSku = new Map<string, { qty: number; name: string }>();
      for (const row of deliveredLineItems) {
        const sku = String(row.sku ?? "").trim();
        const receivedQtyRaw = Number(row.quantity_received ?? 0);
        const orderedQtyRaw = Number(row.quantity_ordered ?? 0);
        const qty = Math.max(0, receivedQtyRaw > 0 ? receivedQtyRaw : orderedQtyRaw);
        if (qty <= 0) continue;

        const normalizedSku = normalizeSku(sku);
        const normalizedName = normalizeName(row.product_name);
        const canonicalSku =
          (normalizedSku ? canonicalSkuByNormalizedSku.get(normalizedSku) : undefined) ??
          (normalizedName ? canonicalSkuByNormalizedName.get(normalizedName) : undefined) ??
          sku;

        if (!canonicalSku) {
          unmatchedDeliveredRows.push({
            sku,
            productName: String(row.product_name ?? ""),
            qty,
          });
          continue;
        }

        const prev = deliveredByCanonicalSku.get(canonicalSku);
        deliveredByCanonicalSku.set(canonicalSku, {
          qty: (prev?.qty ?? 0) + qty,
          name: String(row.product_name ?? prev?.name ?? canonicalSku),
        });
      }

      // Merge delivered PO fallback without double counting against receiving:
      // take the higher quantity per SKU source.
      for (const [sku, delivered] of deliveredByCanonicalSku) {
        const prev = receivedByCanonicalSku.get(sku);
        if (!prev) {
          receivedByCanonicalSku.set(sku, { qty: delivered.qty, name: delivered.name, label: "" });
          continue;
        }
        if (delivered.qty > prev.qty) {
          receivedByCanonicalSku.set(sku, {
            qty: delivered.qty,
            name: prev.name || delivered.name,
            label: prev.label,
          });
        }
      }

      if (receivedByCanonicalSku.size === 0) return { created: 0, skipped: 0 };

      const allocatedBySku = new Map<string, number>();
      const allocationRowsBySku = new Map<string, Array<{ id: string; quantity: number; status: string }>>();
      for (const row of allocationRows) {
        if (row.status === "Unallocated") continue;
        const rawSku = String(row.sku ?? "").trim();
        const sku = rawSku || (String(row.product_name ?? "").trim() || rawSku);
        if (!sku) continue;
        const normalizedSku = normalizeSku(sku);
        const canonicalSku = canonicalSkuByNormalizedSku.get(normalizedSku) ?? rawSku;
        if (!canonicalSku) continue;
        const rowQuantity = Math.max(0, Number(row.quantity ?? 0));
        allocatedBySku.set(canonicalSku, (allocatedBySku.get(canonicalSku) ?? 0) + rowQuantity);
        const rowId = String(row.id ?? "").trim();
        if (!rowId) continue;
        const bucket = allocationRowsBySku.get(canonicalSku) ?? [];
        bucket.push({
          id: rowId,
          quantity: rowQuantity,
          status: String(row.status ?? ""),
        });
        allocationRowsBySku.set(canonicalSku, bucket);
      }

      const targetAllocationBySku = new Map<string, number>();
      for (const [sku, received] of receivedByCanonicalSku) {
        const requiredQty = requiredByCanonicalSku.get(sku);
        if (requiredQty === undefined) {
          unmatchedDeliveredRows.push({
            sku,
            productName: received.name,
            qty: received.qty,
          });
          continue;
        }
        targetAllocationBySku.set(sku, Math.max(0, Math.min(received.qty, requiredQty)));
      }

      // Cleanup pass: trim stale over-allocations down to target before creating new rows.
      // Only trim SKUs that have actual receiving records — if a SKU was manually allocated
      // without a corresponding PO receipt, it should NOT be treated as over-allocated.
      for (const [sku, allocatedQty] of allocatedBySku) {
        if (!receivedByCanonicalSku.has(sku)) continue;
        const targetQty = targetAllocationBySku.get(sku) ?? 0;
        let excess = Math.max(0, allocatedQty - targetQty);
        if (excess <= 0) continue;
        const candidateRows = (allocationRowsBySku.get(sku) ?? [])
          .filter((row) => row.quantity > 0)
          // Never auto-trim allocations that have been confirmed by the warehouse
          // (Ready for Install or Installed) — only trim Reserved rows.
          .filter((row) => row.status === "Reserved")
          .sort((a, b) => {
            const rank = (status: string) => (status === "Reserved" ? 0 : status === "Ready for Install" ? 1 : 2);
            return rank(a.status) - rank(b.status);
          });
        for (const row of candidateRows) {
          if (excess <= 0) break;
          if (row.quantity <= excess) {
            const deleted = await WarehouseService.deleteJobAllocation(row.id);
            if (deleted) {
              excess -= row.quantity;
            }
            continue;
          }
          const nextQty = Math.max(0, row.quantity - excess);
          const updated = await WarehouseService.updateJobAllocation(row.id, { quantity: nextQty });
          if (updated) {
            excess = 0;
          }
        }
        allocatedBySku.set(sku, targetQty);
      }

      // Re-read live active allocation totals after cleanup so subsequent create
      // calculations use DB truth (not optimistic local assumptions). This avoids
      // repeated trigger violations when prior over-allocation rows remain.
      {
        const liveAllocRes = await supabase
          .from("job_allocations")
          .select("sku, quantity, status")
          .eq("job_id", resolvedJobId)
          .in("status", ["Reserved", "Ready for Install"]);
        if (liveAllocRes.error) {
          console.warn("[WarehouseService] reconcile live allocation refresh failed:", liveAllocRes.error.message);
          return { created: 0, skipped: 0 };
        }
        allocatedBySku.clear();
        for (const row of liveAllocRes.data ?? []) {
          const rawSku = String(row.sku ?? "").trim();
          if (!rawSku) continue;
          const normalizedSku = normalizeSku(rawSku);
          const canonicalSku = canonicalSkuByNormalizedSku.get(normalizedSku) ?? rawSku;
          if (!canonicalSku) continue;
          allocatedBySku.set(
            canonicalSku,
            (allocatedBySku.get(canonicalSku) ?? 0) + Math.max(0, Number(row.quantity ?? 0)),
          );
        }
      }

      const fallbackJobLabel = typeof jobRes.data?.job_number === "string" && jobRes.data.job_number
        ? jobRes.data.job_number
        : resolvedJobId;

      let created = 0;
      let skipped = 0;
      for (const [sku, received] of receivedByCanonicalSku) {
        const requiredQty = requiredByCanonicalSku.get(sku);
        // When job_materials has no requirement for this SKU, fall back to the
        // received quantity so delivered materials always land in job_allocations.
        const effectiveQty = requiredQty ?? received.qty;
        if (effectiveQty <= 0) {
          skipped += 1;
          continue;
        }
        const allocatedQty = allocatedBySku.get(sku) ?? 0;
        const targetAllocationQty = Math.min(received.qty, effectiveQty);
        const delta = Math.max(0, targetAllocationQty - allocatedQty);
        if (delta <= 0) {
          skipped += 1;
          continue;
        }
        const createdAllocation = await WarehouseService.createJobAllocation({
          jobId: resolvedJobId,
          jobLabel: received.label || fallbackJobLabel,
          sku,
          productName: received.name,
          quantity: delta,
          status: "Reserved",
          allocatedAt: new Date().toISOString(),
          allocatedBy: "system-reconcile",
        });
        if (createdAllocation) {
          created += 1;
        } else {
          skipped += 1;
        }
      }

      if (unmatchedDeliveredRows.length > 0) {
        console.warn("[WarehouseService] Delivered rows could not be matched to job_materials during allocation reconcile:", {
          jobId: resolvedJobId,
          rows: unmatchedDeliveredRows,
        });
      }

      // Always rebuild after reconcile so the job's missingItems reflects the
      // final allocation state, even if createJobAllocation returned null
      // (e.g. due to a DB constraint or no-op delta).
      await rebuildJobMaterialsList(resolvedJobId);

      const { data: jobOrgRow } = await supabase
        .from('jobs')
        .select('organization_id, job_number')
        .eq('id', resolvedJobId)
        .maybeSingle();

      const jobLabel = String(jobOrgRow?.job_number ?? jobId);

      void SystemUpdateService.post({
        domain:   'warehouse',
        event:    'materials_staged',
        orgId:    jobOrgRow?.organization_id ?? undefined,
        body:     `System: Materials Staged for Job — ${jobLabel}`,
        metadata: {
          title:  'Materials Staged for Job',
          detail: `Job ${jobLabel}`,
          action: { label: 'View Warehouse', path: '/warehouse' },
        },
      });

      return { created, skipped };
    } catch (err) {
      console.error("[WarehouseService] Exception reconciling job allocations:", err);
      return { created: 0, skipped: 0 };
    }
  },

  async fetchShipments(): Promise<Shipment[]> {
    try {
      return await withWarehouseServiceCache("shipments:all", async () => {
        const { data, error } = await supabase
          .from("shipments")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) {
          console.error("[WarehouseService] Error fetching shipments:", error.message);
          return [];
        }

        if (!data || data.length === 0) {
          return [];
        }

        return data.map((item: DbShipment) => mapDbShipment(item));
      });
    } catch (err) {
      console.error("[WarehouseService] Exception fetching shipments:", err);
      return [];
    }
  },

  async fetchReceivingRecords(): Promise<ReceivingRecord[]> {
    try {
      return await withWarehouseServiceCache("receiving_records:all", async () => {
        const { data, error } = await supabase
          .from("receiving_records")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) {
          console.error("[WarehouseService] Error fetching receiving records:", error.message);
          return [];
        }

        if (!data || data.length === 0) {
          return [];
        }

        return data.map((item: DbReceivingRecord) => mapDbReceivingRecord(item));
      });
    } catch (err) {
      console.error("[WarehouseService] Exception fetching receiving records:", err);
      return [];
    }
  },

  async fetchJobAllocations(): Promise<JobAllocation[]> {
    try {
      return await withWarehouseServiceCache("job_allocations:all", async () => {
        const { data, error } = await supabase
          .from("job_allocations")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) {
          console.error("[WarehouseService] Error fetching job allocations:", error.message);
          return [];
        }

        if (!data || data.length === 0) {
          return [];
        }

        return data.map((item: DbJobAllocation) => mapDbJobAllocation(item));
      });
    } catch (err) {
      console.error("[WarehouseService] Exception fetching job allocations:", err);
      return [];
    }
  },

  /**
   * Bulk-fetch all job_materials rows for the readiness check.
   * Returns only the minimal fields needed: job_id, sku, quantity_required.
   */
  async fetchAllJobMaterialRequirements(): Promise<Array<{ jobId: string; sku: string; quantityRequired: number }>> {
    try {
      const { data, error } = await supabase
        .from("job_materials")
        .select("job_id, sku, quantity_required")
        .gt("quantity_required", 0);
      if (error) {
        console.error("[WarehouseService] Error fetching all job_materials requirements:", error.message);
        return [];
      }
      return (data ?? []).map((row) => ({
        jobId: String(row.job_id ?? "").trim(),
        sku: String(row.sku ?? "").trim(),
        quantityRequired: Math.max(0, Number(row.quantity_required ?? 0)),
      })).filter((r) => r.jobId && r.sku && r.quantityRequired > 0);
    } catch (err) {
      console.error("[WarehouseService] Exception fetching all job_materials requirements:", err);
      return [];
    }
  },

  async fetchJobAllocationsForJob(jobId: string): Promise<JobAllocation[]> {
    try {
      const resolvedJobId = await resolveJobId(jobId);
      if (!resolvedJobId) return [];

      const data = await fetchJobAllocationRowsForJob(resolvedJobId);
      return data.map((item: DbJobAllocation) => mapDbJobAllocation(item));
    } catch (err) {
      console.error("[WarehouseService] Exception fetching scoped job allocations:", err);
      return [];
    }
  },

  async createShipment(shipment: Omit<Shipment, "id" | "createdAt">): Promise<Shipment | null> {
    try {
      const organizationId = await getCurrentOrganizationId();
      const basePayload = {
        organization_id: organizationId,
        po_id: shipment.poId,
        po_number: shipment.poNumber,
        vendor_id: shipment.vendorId || null,
        vendor_name: shipment.vendorName,
        items: shipment.items,
        status: shipment.status,
        expected_delivery: shipment.expectedDelivery || null,
        delivered_at: shipment.deliveredAt || null,
        received_at: shipment.receivedAt || null,
      };

      const { data, error } = await supabase
        .from("shipments")
        .insert(basePayload)
        .select()
        .single();

      if (error) {
        console.error("[WarehouseService] Error creating shipment:", error.message, error);
        return null;
      }

      await Promise.all(
        shipment.items.map((item) =>
          syncWarehouseStock(item.sku, { incomingStockDelta: item.expectedQuantity - item.receivedQuantity }),
        ),
      );

      const purposeField = (shipment as any).purpose ?? (shipment as any).type ?? '';
      const isLoadout = ['load_out', 'loadout', 'LOAD_OUT'].includes(String(purposeField));

      if (isLoadout) {
        void SystemUpdateService.post({
          domain:   'warehouse',
          event:    'loadout_confirmed',
          orgId:    organizationId,
          body:     `System: Load-out Confirmed`,
          metadata: {
            title:  'Load-out Confirmed',
            detail: `Shipment created`,
            action: { label: 'View Warehouse', path: '/warehouse' },
          },
        });
      } else {
        void SystemUpdateService.post({
          domain: 'warehouse',
          event:  'shipment_created',
          orgId:  organizationId,
          body:   `System: Incoming Shipment Created`,
        });
      }

      return {
        id: data.id,
        poId: data.po_id,
        poNumber: data.po_number,
        vendorId: data.vendor_id,
        vendorName: data.vendor_name,
        items: data.items || [],
        status: mapShipmentStatus(data.status),
        createdAt: data.created_at,
        expectedDelivery: data.expected_delivery || undefined,
        deliveredAt: data.delivered_at || undefined,
        receivedAt: data.received_at || undefined,
      };
    } catch (err) {
      console.error("[WarehouseService] Exception creating shipment:", err);
      return null;
    }
  },

  async createReceivingRecord(record: Omit<ReceivingRecord, "id">): Promise<ReceivingRecord | null> {
    try {
      const [resolvedJobId, organizationId] = await Promise.all([
        resolveJobId(record.jobId),
        getCurrentOrganizationId(),
      ]);
      const basePayload = {
        organization_id: organizationId,
        po_id: record.poId,
        po_number: record.poNumber,
        vendor_name: record.vendorName,
        job_id: resolvedJobId,
        job_label: record.jobLabel,
        sku: record.sku,
        product_name: record.productName,
        expected_quantity: record.expectedQuantity,
        received_quantity: record.receivedQuantity,
        status: record.status,
        received_at: record.receivedAt || null,
        received_by: record.receivedBy || null,
        shipment_id: record.shipmentId || null,
        discrepancy_type: record.discrepancy?.type || null,
        discrepancy_notes: record.discrepancy?.notes || null,
      };

      const { data, error } = await supabase
        .from("receiving_records")
        .insert(basePayload)
        .select()
        .single();

      if (error) {
        console.error("[WarehouseService] Error creating receiving record:", error.message);
        return null;
      }

      return {
        id: data.id,
        poId: data.po_id,
        poNumber: data.po_number,
        vendorName: data.vendor_name,
        // Keep UI continuity with the job identifier used by callers (JOB-###).
        jobId: record.jobId,
        jobLabel: data.job_label,
        sku: data.sku,
        productName: data.product_name,
        expectedQuantity: data.expected_quantity,
        receivedQuantity: data.received_quantity,
        status: mapReceivingStatus(data.status),
        receivedAt: data.received_at || undefined,
        receivedBy: data.received_by || undefined,
        shipmentId: data.shipment_id || undefined,
      };
    } catch (err) {
      console.error("[WarehouseService] Exception creating receiving record:", err);
      return null;
    }
  },

  async updateReceivingRecord(recordId: string, updates: Partial<ReceivingRecord>): Promise<boolean> {
    try {
      const currentRecord = await supabase
        .from("receiving_records")
        .select("job_id, sku, received_quantity")
        .eq("id", recordId)
        .maybeSingle();

      const dbUpdates: Record<string, unknown> = {};
      if (updates.receivedQuantity !== undefined) dbUpdates.received_quantity = updates.receivedQuantity;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.receivedAt !== undefined) dbUpdates.received_at = updates.receivedAt;
      if (updates.receivedBy !== undefined) dbUpdates.received_by = updates.receivedBy;
      if (updates.discrepancy) {
        dbUpdates.discrepancy_type = updates.discrepancy.type;
        dbUpdates.discrepancy_notes = updates.discrepancy.notes;
      }

      const { error } = await supabase
        .from("receiving_records")
        .update(dbUpdates)
        .eq("id", recordId);

      if (error) {
        console.error("[WarehouseService] Error updating receiving record:", error.message);
        return false;
      }

      // Return immediately — the core DB write is done and the UI can refresh.
      // Stock level syncs and job material rebuilds are background bookkeeping;
      // they must not block the receive confirmation dialog.
      const previousQuantity = currentRecord.data?.received_quantity ?? 0;
      const quantityDelta = (updates.receivedQuantity ?? previousQuantity) - previousQuantity;
      const sku = currentRecord.data?.sku;
      const jobId = currentRecord.data?.job_id;
      const lifecycleStage = updates.status === "Fully Received" ? "Received" : "Ordered";

      void (async () => {
        try {
          if (quantityDelta !== 0 && sku) {
            await Promise.all([
              syncProductRegistryStock(sku, { quantityOnHandDelta: quantityDelta }),
              syncStockLevels(sku, { quantityOnHandDelta: quantityDelta }),
              syncWarehouseStock(sku, { quantityOnHandDelta: quantityDelta, incomingStockDelta: -quantityDelta }),
              ...(jobId ? [syncJobMaterialState(jobId, sku, { lifecycleStage, inventoryStatus: "In Stock" })] : []),
            ]);
          }
          if (jobId) {
            await syncJobMaterialReadiness(jobId);
            await rebuildJobMaterialsList(jobId);
          }
        } catch (bgErr) {
          console.warn("[WarehouseService] Background sync failed after receiving record update:", bgErr);
        }
      })();

      return true;
    } catch (err) {
      console.error("[WarehouseService] Exception updating receiving record:", err);
      return false;
    }
  },

  async createJobAllocation(allocation: Omit<JobAllocation, "id">): Promise<JobAllocation | null> {
    try {
      const canUseProductId = await supportsProductIdColumns();
      const resolvedJobId = await resolveJobId(allocation.jobId);
      const resolvedSku = String(allocation.sku ?? "").trim();
      if (!resolvedJobId || !resolvedSku) return null;
      const normalizeAllocSku = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
      const resolvedSkuKey = normalizeAllocSku(resolvedSku);
      const resolvedProductId = canUseProductId ? await resolveProductIdForJobSku(resolvedJobId, resolvedSku) : null;
      const requestedQuantity = Math.max(0, Number(allocation.quantity ?? 0));
      if (requestedQuantity <= 0) return null;
      const operationKey = [
        resolvedJobId,
        resolvedProductId || resolvedSkuKey,
        requestedQuantity,
        allocation.status,
        String(allocation.allocatedBy || ""),
      ].join("|");

      return await withWarehouseAllocationOperation(operationKey, async () => {
        invalidateJobWarehouseCache(resolvedJobId);
        const [required, allocationRows] = await Promise.all([
          resolveRequiredQtyForJobSku(resolvedJobId, resolvedSku, resolvedProductId),
          fetchJobAllocationRowsForJob(resolvedJobId),
        ]);

        const requiredQty = required.quantity;
        if (required.source === "metadata" && requiredQty > 0) {
          await ensureJobMaterialRequirementRow(
            resolvedJobId,
            resolvedSku,
            requiredQty,
            String(allocation.productName || required.itemName || resolvedSku),
          );
        }
        const matchingAllocationRows = allocationRows.filter((row) => {
          if (resolvedProductId && String(row.product_id ?? "").trim() === resolvedProductId) return true;
          const rowSku = String(row.sku ?? "");
          return normalizeAllocSku(rowSku) === resolvedSkuKey;
        });
        // Include 'Installed' so materials that have already been installed
        // count against the required quantity. Without this, Installed rows are
        // invisible to the check → remainingQty inflates back to requiredQty →
        // a new Reserved allocation is created, restarting the allocation loop.
        const currentlyAllocatedQty = matchingAllocationRows
          .filter((row) => row.status === "Reserved" || row.status === "Ready for Install" || row.status === "Installed")
          .reduce((sum, row) => sum + Math.max(0, Number(row.quantity ?? 0)), 0);
        const isOverride = String(allocation.allocatedBy ?? "").toLowerCase().includes("override");
        const isSystemReconcile = allocation.allocatedBy === "system-reconcile";
        if (!isOverride && !isSystemReconcile && requiredQty <= 0) {
          return null;
        }
        const remainingQty = requiredQty > 0 ? Math.max(0, requiredQty - currentlyAllocatedQty) : requestedQuantity;
        const quantityToApply = (isOverride || isSystemReconcile) ? requestedQuantity : Math.min(requestedQuantity, remainingQty);
        if (quantityToApply <= 0) {
          return null;
        }

        // Only merge into active (non-Installed) rows. Installed rows must not be
        // mutated — fall through to INSERT so a new Reserved allocation is created.
        const existingAllocationRow =
          matchingAllocationRows.find((row) => row.status === "Reserved" || row.status === "Ready for Install");
        if (existingAllocationRow) {
          const existingStatus = String(existingAllocationRow.status ?? "");
          const existingQty = Math.max(0, Number(existingAllocationRow.quantity ?? 0));
          const additiveBaseQty = existingStatus === "Unallocated" ? 0 : existingQty;
          const unclampedNextQty = additiveBaseQty + quantityToApply;
          const nextQty = (!isOverride && requiredQty > 0) ? Math.min(unclampedNextQty, requiredQty) : unclampedNextQty;
          const appliedDelta = Math.max(0, nextQty - additiveBaseQty);
          if (appliedDelta <= 0) return null;
          const nextStatus =
            allocation.status === "Ready for Install" || existingAllocationRow.status === "Ready for Install"
              ? "Ready for Install"
              : allocation.status;

          const { data: updatedData, error: updateError } = await supabase
            .from("job_allocations")
            .update({
              quantity: nextQty,
              status: nextStatus,
              allocated_at: allocation.allocatedAt || existingAllocationRow.allocated_at || null,
              allocated_by: allocation.allocatedBy || existingAllocationRow.allocated_by || null,
              product_name: allocation.productName || existingAllocationRow.product_name || null,
              ...(canUseProductId ? { product_id: resolvedProductId || (existingAllocationRow as { product_id?: string | null }).product_id || null } : {}),
              job_label: allocation.jobLabel || existingAllocationRow.job_label || null,
            })
            .eq("id", existingAllocationRow.id)
            .select()
            .single();

          if (updateError) {
            if (updateError.code === "23514") {
              return null;
            }
            console.error("[WarehouseService] Error merging existing job allocation:", updateError.message);
            return null;
          }

          invalidateJobWarehouseCache(resolvedJobId);
          await Promise.all([
            syncStockLevels(resolvedSku, { quantityReservedDelta: appliedDelta }),
            syncWarehouseStock(resolvedSku, { quantityReservedDelta: appliedDelta }),
            syncJobMaterialState(String(resolvedJobId ?? allocation.jobId), resolvedSku, {
              quantityAllocatedDelta: appliedDelta,
              lifecycleStage: nextStatus === "Ready for Install" ? "Assigned" : "Assigned",
              inventoryStatus: "Allocated",
            }),
          ]);
          await rebuildJobMaterialsList(String(resolvedJobId ?? allocation.jobId));

          return {
            id: updatedData.id,
            jobId: updatedData.job_id,
            jobLabel: updatedData.job_label,
            sku: updatedData.sku,
            productName: updatedData.product_name,
            quantity: updatedData.quantity,
            status: mapAllocationStatus(updatedData.status),
            allocatedAt: updatedData.allocated_at || undefined,
            allocatedBy: updatedData.allocated_by || undefined,
          };
        }

        const organizationId = await getCurrentOrganizationId();
        const basePayload = {
          job_id: resolvedJobId,
          job_label: allocation.jobLabel,
          sku: resolvedSku,
          product_name: allocation.productName,
          ...(canUseProductId ? { product_id: resolvedProductId } : {}),
          quantity: quantityToApply,
          status: allocation.status,
          allocated_at: allocation.allocatedAt || null,
          allocated_by: allocation.allocatedBy || null,
          organization_id: organizationId,
        };

        const { data, error } = await supabase
          .from("job_allocations")
          .insert(basePayload)
          .select()
          .single();

        if (error) {
          if (error.code === "23514") {
            return null;
          }
          console.error("[WarehouseService] Error creating job allocation:", error.message);
          return null;
        }

        invalidateJobWarehouseCache(resolvedJobId);
        await Promise.all([
          syncStockLevels(resolvedSku, { quantityReservedDelta: quantityToApply }),
          syncWarehouseStock(resolvedSku, { quantityReservedDelta: quantityToApply }),
          syncJobMaterialState(String(resolvedJobId ?? allocation.jobId), resolvedSku, {
            quantityAllocatedDelta: quantityToApply,
            lifecycleStage: allocation.status === "Ready for Install" ? "Assigned" : "Assigned",
            inventoryStatus: "Allocated",
          }),
        ]);
        await rebuildJobMaterialsList(String(resolvedJobId ?? allocation.jobId));

        return {
          id: data.id,
          jobId: data.job_id,
          jobLabel: data.job_label,
          sku: data.sku,
          productName: data.product_name,
          quantity: data.quantity,
          status: mapAllocationStatus(data.status),
          allocatedAt: data.allocated_at || undefined,
          allocatedBy: data.allocated_by || undefined,
        };
      });
    } catch (err) {
      console.error("[WarehouseService] Exception creating job allocation:", err);
      return null;
    }
  },

  async updateJobAllocation(allocationId: string, updates: Partial<JobAllocation>): Promise<boolean> {
    try {
      const canUseProductId = await supportsProductIdColumns();
      const currentAllocation = await supabase
        .from("job_allocations")
        .select(canUseProductId ? "id, job_id, sku, quantity, status, allocated_by, product_id" : "id, job_id, sku, quantity, status, allocated_by")
        .eq("id", allocationId)
        .maybeSingle();

      if (!currentAllocation.data) return false;

      const normalizeAllocSku = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
      const resolvedJobId = String(currentAllocation.data.job_id ?? "").trim();
      const resolvedSku = String(currentAllocation.data.sku ?? "").trim();
      const resolvedProductId = canUseProductId ? await resolveProductIdForJobSku(resolvedJobId, resolvedSku) : null;
      const requestedStatus = updates.status ?? mapAllocationStatus(String(currentAllocation.data.status ?? ""));
      const requestedQuantity = Math.max(0, Number(updates.quantity ?? currentAllocation.data.quantity ?? 0));
      const isActiveStatus = requestedStatus === "Reserved" || requestedStatus === "Ready for Install";
      const isOverride =
        String(updates.allocatedBy ?? "").toLowerCase().includes("override")
        || String(currentAllocation.data.allocated_by ?? "").toLowerCase().includes("override");

      let nextQuantity = requestedQuantity;
      const isQuantityBeingChanged = updates.quantity !== undefined;
      if (!isOverride && isActiveStatus && resolvedJobId && resolvedSku && isQuantityBeingChanged) {
        invalidateJobWarehouseCache(resolvedJobId);
        const [required, otherAllocations] = await Promise.all([
          resolveRequiredQtyForJobSku(resolvedJobId, resolvedSku, resolvedProductId),
          fetchJobAllocationRowsForJob(resolvedJobId),
        ]);

        const requiredQty = required.quantity;
        if (required.source === "metadata" && requiredQty > 0) {
          await ensureJobMaterialRequirementRow(
            resolvedJobId,
            resolvedSku,
            requiredQty,
            String(updates.allocatedBy || resolvedSku),
          );
        }
        if (requiredQty <= 0) return false;
        const skuKey = normalizeAllocSku(resolvedSku);
        const allocatedByOthers = otherAllocations
          .filter((row) => String(row.id ?? "") !== String(currentAllocation.data?.id ?? ""))
          .filter((row) => row.status === "Reserved" || row.status === "Ready for Install")
          .filter((row) =>
            resolvedProductId
              ? String(row.product_id ?? "").trim() === resolvedProductId
              : normalizeAllocSku(String(row.sku ?? "")) === skuKey,
          )
          .reduce((sum, row) => sum + Math.max(0, Number(row.quantity ?? 0)), 0);
        const remainingQty = Math.max(0, requiredQty - allocatedByOthers);
        nextQuantity = Math.min(requestedQuantity, remainingQty);
      }

      const dbUpdates: Record<string, unknown> = {};
      if (updates.quantity !== undefined || (!isOverride && isActiveStatus && isQuantityBeingChanged)) dbUpdates.quantity = nextQuantity;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.allocatedAt !== undefined) dbUpdates.allocated_at = updates.allocatedAt;
      if (updates.allocatedBy !== undefined) dbUpdates.allocated_by = updates.allocatedBy;

      const { error } = await supabase
        .from("job_allocations")
        .update(dbUpdates)
        .eq("id", allocationId);

      if (error) {
        console.error("[WarehouseService] Error updating job allocation:", error.message);
        return false;
      }

      if (resolvedJobId) {
        invalidateJobWarehouseCache(resolvedJobId);
      }

      const previousQuantity = Number(currentAllocation.data?.quantity ?? 0);
      const quantityDelta = nextQuantity - previousQuantity;
      if (quantityDelta !== 0 && currentAllocation.data?.sku && currentAllocation.data?.job_id) {
        await Promise.all([
          syncStockLevels(currentAllocation.data.sku, { quantityReservedDelta: quantityDelta }),
          syncWarehouseStock(currentAllocation.data.sku, { quantityReservedDelta: quantityDelta }),
          syncJobMaterialState(currentAllocation.data.job_id, currentAllocation.data.sku, {
            quantityAllocatedDelta: quantityDelta,
            lifecycleStage: "Assigned",
            inventoryStatus: "Allocated",
          }),
        ]);
        await rebuildJobMaterialsList(currentAllocation.data.job_id);
      }

      // When an allocation is marked Installed, decrement physical stock so
      // available_stock in v_warehouse_stock_ui reflects items that have left
      // the warehouse. Without this, available_stock inflates (items appear
      // back on the shelf) because the view formula excludes Installed rows.
      if (
        currentAllocation.data?.sku &&
        updates.status === "Installed" &&
        currentAllocation.data.status !== "Installed"
      ) {
        const installedQty = Number(currentAllocation.data.quantity ?? 0);
        if (installedQty > 0) {
          await Promise.all([
            syncProductRegistryStock(currentAllocation.data.sku, { quantityOnHandDelta: -installedQty }),
            syncStockLevels(currentAllocation.data.sku, { quantityOnHandDelta: -installedQty }),
            syncWarehouseStock(currentAllocation.data.sku, { quantityOnHandDelta: -installedQty }),
          ]);
        }
      }

      if (
        currentAllocation.data?.job_id &&
        currentAllocation.data?.sku &&
        updates.status === "Ready for Install" &&
        currentAllocation.data.status !== "Ready for Install"
      ) {
        await syncJobMaterialState(currentAllocation.data.job_id, currentAllocation.data.sku, {
          quantityStagedDelta: currentAllocation.data.quantity ?? 0,
          lifecycleStage: "Assigned",
          inventoryStatus: "Allocated",
        });
        await rebuildJobMaterialsList(currentAllocation.data.job_id);
      }

      // Sync job.material_readiness whenever an allocation status changes.
      if (currentAllocation.data?.job_id) {
        await syncJobMaterialReadiness(currentAllocation.data.job_id);
        await rebuildJobMaterialsList(currentAllocation.data.job_id);
      }

      return true;
    } catch (err) {
      console.error("[WarehouseService] Exception updating job allocation:", err);
      return false;
    }
  },

  async deleteJobAllocation(allocationId: string): Promise<boolean> {
    try {
      const currentAllocation = await supabase
        .from("job_allocations")
        .select("job_id, sku, quantity")
        .eq("id", allocationId)
        .maybeSingle();

      const { error } = await supabase
        .from("job_allocations")
        .delete()
        .eq("id", allocationId);

      if (error) {
        console.error("[WarehouseService] Error deleting job allocation:", error.message);
        return false;
      }

      if (currentAllocation.data?.sku) {
        await Promise.all([
          syncStockLevels(currentAllocation.data.sku, { quantityReservedDelta: -(currentAllocation.data.quantity ?? 0) }),
          syncWarehouseStock(currentAllocation.data.sku, { quantityReservedDelta: -(currentAllocation.data.quantity ?? 0) }),
        ]);
      }

      if (currentAllocation.data?.job_id && currentAllocation.data?.sku) {
        await syncJobMaterialState(currentAllocation.data.job_id, currentAllocation.data.sku, {
          quantityAllocatedDelta: -(currentAllocation.data.quantity ?? 0),
          quantityStagedDelta: -(currentAllocation.data.quantity ?? 0),
          lifecycleStage: "Received",
          inventoryStatus: "Reserved",
        });
        // rebuildJobMaterialsList is called once below — no duplicate here.
      }

      if (currentAllocation.data?.job_id) {
        await syncJobMaterialReadiness(currentAllocation.data.job_id);
        await rebuildJobMaterialsList(currentAllocation.data.job_id);
      }

      return true;
    } catch (err) {
      console.error("[WarehouseService] Exception deleting job allocation:", err);
      return false;
    }
  },

  /**
   * Sync all shipments for a PO to the given status.
   * Called after a PO status change (e.g. from MaterialsGateCard) so that
   * v_warehouse_shipments_ui reflects the new state immediately.
   */
  async syncShipmentsStatusForPo(poId: string, status: ShipmentStatus): Promise<void> {
    const dbUpdates: Record<string, unknown> = { status };
    if (status === "In Transit") dbUpdates.movement_stage = "In Transit";
    if (status === "Delivered") {
      dbUpdates.delivered_at = new Date().toISOString();
      dbUpdates.movement_stage = "Staged";
    }
    if (status === "Received") {
      dbUpdates.received_at = new Date().toISOString();
      dbUpdates.movement_stage = "On Site";
    }

    const { error } = await supabase.from("shipments").update(dbUpdates).eq("po_id", poId);
    if (error) console.warn("[WarehouseService] Could not sync shipments for PO:", error.message);
  },

  /**
   * Delete all shipments tied to a PO.
   * shipments.po_id uses ON DELETE SET NULL (not CASCADE), so this must be called explicitly on order reversal.
   */
  async deleteShipmentsForPo(poId: string): Promise<void> {
    const { error } = await supabase.from("shipments").delete().eq("po_id", poId);
    if (error) console.warn("[WarehouseService] Error deleting shipments for PO:", error.message);
  },

  /**
   * Delete all receiving records tied to a PO.
   * receiving_records.po_id uses ON DELETE SET NULL (not CASCADE), so this must be called explicitly on order reversal.
   */
  async deleteReceivingRecordsForPo(poId: string): Promise<void> {
    const { error } = await supabase.from("receiving_records").delete().eq("po_id", poId);
    if (error) console.warn("[WarehouseService] Error deleting receiving records for PO:", error.message);
  },

  /**
   * Delete system-reconcile allocations for a job scoped to the given SKUs,
   * backing out reserved stock via deleteJobAllocation (which handles stock sync).
   * Called when reversing a Delivered PO.
   */
  async deleteJobAllocationsForPoReversal(jobId: string, skus: string[]): Promise<void> {
    if (skus.length === 0) return;
    const normalizedSkus = skus.map((s) => String(s ?? "").trim().toUpperCase()).filter(Boolean);
    const { data: allocs, error: fetchError } = await supabase
      .from("job_allocations")
      .select("id")
      .eq("job_id", jobId)
      .eq("allocated_by", "system-reconcile")
      .in("sku", normalizedSkus);
    if (fetchError) {
      console.warn("[WarehouseService] Could not fetch allocations for PO reversal:", fetchError.message);
      return;
    }
    for (const alloc of allocs ?? []) {
      await WarehouseService.deleteJobAllocation(alloc.id);
    }
  },

  async updateShipmentStatus(shipmentId: string, status: ShipmentStatus, deliveredAt?: string, skipJobSync?: boolean): Promise<boolean> {
    try {
      const dbUpdates: Record<string, unknown> = { status };
      if (status === "Delivered") dbUpdates.delivered_at = deliveredAt ?? new Date().toISOString();
      if (status === "Received") dbUpdates.received_at = new Date().toISOString();
      // Auto-advance movement stage based on shipment workflow.
      if (status === "In Transit") dbUpdates.movement_stage = "In Transit";
      if (status === "Delivered")  dbUpdates.movement_stage = "Staged";
      if (status === "Received")   dbUpdates.movement_stage = "On Site";

      const { error } = await supabase
        .from("shipments")
        .update(dbUpdates)
        .eq("id", shipmentId);

      if (error) {
        console.error("[WarehouseService] Error updating shipment:", error.message);
        return false;
      }

      // Sync the linked PO and job when shipment status changes from Incoming Shipments.
      // purchase_orders.status valid values: Draft | Submitted | Confirmed | Delivered | In Transit | Received
      // (extended via 20260716_po_status_extend.sql migration)
      if (status === "In Transit" || status === "Delivered" || status === "Received") {
        const { data: shipment } = await supabase
          .from("shipments")
          .select("po_id")
          .eq("id", shipmentId)
          .single();

        if (shipment?.po_id) {
          const poUpdate: Record<string, unknown> = { status };
          if (status === "Delivered") poUpdate.delivered_at = deliveredAt ?? new Date().toISOString();

          const { data: po, error: poError } = await supabase
            .from("purchase_orders")
            .update(poUpdate)
            .eq("id", shipment.po_id)
            .select("job_id")
            .single();

          if (poError) {
            console.warn("[WarehouseService] Could not sync PO status:", poError.message);
          }

          // After receiving, reconcile allocations and material readiness for the linked job.
          // skipJobSync=true when called from receiveShipmentItems — reconciliation already
          // ran inside that loop, so we only update the shipment + PO status here.
          if (status === "Received" && !skipJobSync) {
            const jobId = po?.job_id;
            if (jobId) {
              try {
                await WarehouseService.reconcileJobAllocationsFromReceiving(jobId);
              } catch (err) {
                console.warn("[WarehouseService] Allocation reconcile failed after shipment received:", err);
              }
              try {
                await syncJobMaterialReadiness(jobId);
              } catch (err) {
                console.warn("[WarehouseService] syncJobMaterialReadiness failed after shipment received:", err);
              }
            }
          }
        }
      }

      return true;
    } catch (err) {
      console.error("[WarehouseService] Exception updating shipment:", err);
      return false;
    }
  },

  async repairOverAllocations(): Promise<{ jobsScanned: number; rowsTouched: number; quantityTrimmed: number }> {
    const normalizeAllocSku = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
    const withTimeout = async <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms);
      });
      try {
        return await Promise.race([promise, timeout]);
      } finally {
        if (timer) clearTimeout(timer);
      }
    };
    const computeOverageForJob = async (jobId: string): Promise<number> => {
      const [materialsRes, allocationsRes] = await Promise.all([
        supabase
          .from("job_materials")
          .select("sku, quantity_required")
          .eq("job_id", jobId),
        supabase
          .from("job_allocations")
          .select("sku, quantity, status")
          .eq("job_id", jobId)
          .in("status", ["Reserved", "Ready for Install"]),
      ]);
      if (materialsRes.error || allocationsRes.error) return 0;

      const requiredBySku = new Map<string, number>();
      for (const row of materialsRes.data ?? []) {
        const sku = String(row.sku ?? "").trim();
        const key = normalizeAllocSku(sku);
        if (!key) continue;
        requiredBySku.set(key, (requiredBySku.get(key) ?? 0) + Math.max(0, Number(row.quantity_required ?? 0)));
      }

      const allocatedBySku = new Map<string, number>();
      for (const row of allocationsRes.data ?? []) {
        const sku = String(row.sku ?? "").trim();
        const key = normalizeAllocSku(sku);
        if (!key) continue;
        allocatedBySku.set(key, (allocatedBySku.get(key) ?? 0) + Math.max(0, Number(row.quantity ?? 0)));
      }

      let over = 0;
      for (const [sku, allocated] of allocatedBySku.entries()) {
        const required = Math.max(0, requiredBySku.get(sku) ?? 0);
        over += Math.max(0, allocated - required);
      }
      return over;
    };
    const computeUnderageForJob = async (jobId: string): Promise<number> => {
      const [materialsRes, allocationsRes] = await Promise.all([
        supabase
          .from("job_materials")
          .select("sku, quantity_required")
          .eq("job_id", jobId),
        supabase
          .from("job_allocations")
          .select("sku, quantity, status")
          .eq("job_id", jobId)
          .in("status", ["Reserved", "Ready for Install"]),
      ]);
      if (materialsRes.error || allocationsRes.error) return 0;

      const requiredBySku = new Map<string, number>();
      for (const row of materialsRes.data ?? []) {
        const sku = String(row.sku ?? "").trim();
        const key = normalizeAllocSku(sku);
        if (!key) continue;
        requiredBySku.set(key, (requiredBySku.get(key) ?? 0) + Math.max(0, Number(row.quantity_required ?? 0)));
      }

      const allocatedBySku = new Map<string, number>();
      for (const row of allocationsRes.data ?? []) {
        const sku = String(row.sku ?? "").trim();
        const key = normalizeAllocSku(sku);
        if (!key) continue;
        allocatedBySku.set(key, (allocatedBySku.get(key) ?? 0) + Math.max(0, Number(row.quantity ?? 0)));
      }

      let under = 0;
      for (const [sku, required] of requiredBySku.entries()) {
        const allocated = Math.max(0, allocatedBySku.get(sku) ?? 0);
        under += Math.max(0, required - allocated);
      }
      return under;
    };

    try {
      const allocationsRes = await supabase
        .from("job_allocations")
        .select("job_id, sku, quantity, status")
        .in("status", ["Reserved", "Ready for Install"]);
      if (allocationsRes.error) {
        console.error("[WarehouseService] repairOverAllocations allocations fetch failed:", allocationsRes.error.message);
        return { jobsScanned: 0, rowsTouched: 0, quantityTrimmed: 0 };
      }

      const activeAllocations = (allocationsRes.data ?? []).filter((row) => {
        const jobId = String(row.job_id ?? "").trim();
        const sku = String(row.sku ?? "").trim();
        return jobId.length > 0 && sku.length > 0 && Number(row.quantity ?? 0) > 0;
      });
      const receivingRes = await supabase
        .from("receiving_records")
        .select("job_id, received_quantity, status")
        .or("status.eq.Fully Received,received_quantity.gt.0");
      const deliveredJobsRes = await supabase
        .from("purchase_orders")
        .select("job_id, status")
        .eq("status", "Delivered");
      const receivingJobIds = (receivingRes.data ?? [])
        .map((row) => String(row.job_id ?? "").trim())
        .filter((id) => id.length > 0);
      const deliveredJobIds = (deliveredJobsRes.data ?? [])
        .map((row) => String(row.job_id ?? "").trim())
        .filter((id) => id.length > 0);
      const activeAllocationJobIds = activeAllocations.map((row) => String(row.job_id).trim());
      const jobIds = Array.from(new Set([...activeAllocationJobIds, ...receivingJobIds, ...deliveredJobIds]));
      if (jobIds.length === 0) return { jobsScanned: 0, rowsTouched: 0, quantityTrimmed: 0 };
      let rowsTouched = 0;
      let quantityTrimmed = 0;
      const CONCURRENCY = 4;
      for (let i = 0; i < jobIds.length; i += CONCURRENCY) {
        const batch = jobIds.slice(i, i + CONCURRENCY);
        const batchResults = await Promise.all(batch.map(async (jobId) => {
          const beforeOverage = await computeOverageForJob(jobId);
          const beforeUnderage = await computeUnderageForJob(jobId);
          if (beforeOverage <= 0 && beforeUnderage <= 0) return { touched: 0, trimmed: 0 };
          try {
            await withTimeout(WarehouseService.reconcileJobAllocationsFromReceiving(jobId), 20_000, `reconcile ${jobId}`);
            await withTimeout(rebuildJobMaterialsList(jobId), 20_000, `rebuild ${jobId}`);
            const afterOverage = await computeOverageForJob(jobId);
            const afterUnderage = await computeUnderageForJob(jobId);
            const trimmed = Math.max(0, beforeOverage - afterOverage);
            const filled = Math.max(0, beforeUnderage - afterUnderage);
            const touched = trimmed > 0 || filled > 0 ? 1 : 0;
            return { touched, trimmed };
          } catch (jobError) {
            console.warn("[WarehouseService] repairOverAllocations job repair skipped:", { jobId, jobError });
            return { touched: 0, trimmed: 0 };
          }
        }));
        rowsTouched += batchResults.reduce((sum, row) => sum + row.touched, 0);
        quantityTrimmed += batchResults.reduce((sum, row) => sum + row.trimmed, 0);
      }

      return { jobsScanned: jobIds.length, rowsTouched, quantityTrimmed };
    } catch (err) {
      console.error("[WarehouseService] Exception in repairOverAllocations:", err);
      return { jobsScanned: 0, rowsTouched: 0, quantityTrimmed: 0 };
    }
  },

  subscribeToShipments(callback: (shipments: Shipment[]) => void): () => void {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let snapshot: Shipment[] | null = null;

    const refresh = () => {
      if (disposed || inFlight) return;
      inFlight = true;
      recordRealtimeMetric({ source: "warehouse-service", event: "full-refetch", table: "shipments" });
      logWarehouseRealtime("shipments", "refresh start");
      void WarehouseService.fetchShipments()
        .then((shipments) => {
          if (!disposed) {
            snapshot = shipments;
            callback(shipments);
          }
        })
        .finally(() => {
          logWarehouseRealtime("shipments", "refresh done");
          inFlight = false;
        });
    };

    const scheduleRefresh = () => {
      if (disposed) return;
      if (timer) clearTimeout(timer);
      logWarehouseRealtime("shipments", "event received, scheduling refresh");
      timer = setTimeout(refresh, 1_500);
    };

    const applyRealtimePatch = (payload: { eventType?: string; new?: DbShipment; old?: Partial<DbShipment> }) => {
      if (!REALTIME_ROW_PATCHING_ENABLED || !snapshot) {
        scheduleRefresh();
        return;
      }
      const eventType = String(payload.eventType ?? "").toUpperCase();
      const entityId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
      if (!entityId) {
        scheduleRefresh();
        return;
      }
      if (eventType === "DELETE") {
        snapshot = snapshot.filter((shipment) => shipment.id !== entityId);
      } else if (payload.new) {
        const next = mapDbShipment(payload.new);
        const exists = snapshot.some((shipment) => shipment.id === next.id);
        snapshot = exists
          ? snapshot.map((shipment) => shipment.id === next.id ? next : shipment)
          : [next, ...snapshot];
      } else {
        scheduleRefresh();
        return;
      }
      recordRealtimeMetric({
        source: "warehouse-service",
        event: "patch",
        table: "shipments",
        entityId,
        detail: SUPABASE_SYNC_DEBUG ? { eventType } : undefined,
      });
      if (!disposed) callback(snapshot);
    };

    const subscription = supabase
      .channel("shipments-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "shipments" }, (payload) => applyRealtimePatch(asRealtimePatchPayload<DbShipment>(payload)))
      .subscribe();

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  },

  subscribeToReceivingRecords(callback: (records: ReceivingRecord[]) => void): () => void {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let snapshot: ReceivingRecord[] | null = null;

    const refresh = () => {
      if (disposed || inFlight) return;
      inFlight = true;
      recordRealtimeMetric({ source: "warehouse-service", event: "full-refetch", table: "receiving_records" });
      logWarehouseRealtime("receiving_records", "refresh start");
      void WarehouseService.fetchReceivingRecords()
        .then((records) => {
          if (!disposed) {
            snapshot = records;
            callback(records);
          }
        })
        .finally(() => {
          logWarehouseRealtime("receiving_records", "refresh done");
          inFlight = false;
        });
    };

    const scheduleRefresh = () => {
      if (disposed) return;
      if (timer) clearTimeout(timer);
      logWarehouseRealtime("receiving_records", "event received, scheduling refresh");
      timer = setTimeout(refresh, 1_500);
    };

    const applyRealtimePatch = (payload: { eventType?: string; new?: DbReceivingRecord; old?: Partial<DbReceivingRecord> }) => {
      if (!REALTIME_ROW_PATCHING_ENABLED || !snapshot) {
        scheduleRefresh();
        return;
      }
      const eventType = String(payload.eventType ?? "").toUpperCase();
      const entityId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
      if (!entityId) {
        scheduleRefresh();
        return;
      }
      if (eventType === "DELETE") {
        snapshot = snapshot.filter((record) => record.id !== entityId);
      } else if (payload.new) {
        const next = mapDbReceivingRecord(payload.new);
        const exists = snapshot.some((record) => record.id === next.id);
        snapshot = exists
          ? snapshot.map((record) => record.id === next.id ? next : record)
          : [next, ...snapshot];
      } else {
        scheduleRefresh();
        return;
      }
      recordRealtimeMetric({
        source: "warehouse-service",
        event: "patch",
        table: "receiving_records",
        entityId,
        detail: SUPABASE_SYNC_DEBUG ? { eventType } : undefined,
      });
      if (!disposed) callback(snapshot);
    };

    const subscription = supabase
      .channel("receiving-records-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "receiving_records" }, (payload) => applyRealtimePatch(asRealtimePatchPayload<DbReceivingRecord>(payload)))
      .subscribe();

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  },

  subscribeToJobAllocations(callback: (allocations: JobAllocation[]) => void): () => void {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let snapshot: JobAllocation[] | null = null;

    const refresh = () => {
      if (disposed || inFlight) return;
      inFlight = true;
      recordRealtimeMetric({ source: "warehouse-service", event: "full-refetch", table: "job_allocations" });
      logWarehouseRealtime("job_allocations", "refresh start");
      void WarehouseService.fetchJobAllocations()
        .then((allocations) => {
          if (!disposed) {
            snapshot = allocations;
            callback(allocations);
          }
        })
        .finally(() => {
          logWarehouseRealtime("job_allocations", "refresh done");
          inFlight = false;
        });
    };

    const scheduleRefresh = () => {
      if (disposed) return;
      if (timer) clearTimeout(timer);
      logWarehouseRealtime("job_allocations", "event received, scheduling refresh");
      timer = setTimeout(refresh, 1_500);
    };

    const applyRealtimePatch = (payload: { eventType?: string; new?: DbJobAllocation; old?: Partial<DbJobAllocation> }) => {
      if (!REALTIME_ROW_PATCHING_ENABLED || !snapshot) {
        scheduleRefresh();
        return;
      }
      const eventType = String(payload.eventType ?? "").toUpperCase();
      const entityId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
      if (!entityId) {
        scheduleRefresh();
        return;
      }
      if (eventType === "DELETE") {
        snapshot = snapshot.filter((allocation) => allocation.id !== entityId);
      } else if (payload.new) {
        const next = mapDbJobAllocation(payload.new);
        const exists = snapshot.some((allocation) => allocation.id === next.id);
        snapshot = exists
          ? snapshot.map((allocation) => allocation.id === next.id ? next : allocation)
          : [next, ...snapshot];
      } else {
        scheduleRefresh();
        return;
      }
      recordRealtimeMetric({
        source: "warehouse-service",
        event: "patch",
        table: "job_allocations",
        entityId,
        detail: SUPABASE_SYNC_DEBUG ? { eventType } : undefined,
      });
      if (!disposed) callback(snapshot);
    };

    const subscription = supabase
      .channel("job-allocations-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "job_allocations" }, (payload) => applyRealtimePatch(asRealtimePatchPayload<DbJobAllocation>(payload)))
      .subscribe();

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  },

  async reAllocate(
    sourceAllocationId: string,
    destJobId: string,
    destJobLabel: string,
    movedQty: number,
    sku: string,
    productName: string,
    sourceQty: number,
    sourceJobId?: string,
    sourceJobLabel?: string,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      const orgId = await getCurrentOrganizationId();

      let sourceOk: boolean;
      if (movedQty >= sourceQty) {
        sourceOk = await this.deleteJobAllocation(sourceAllocationId);
      } else {
        sourceOk = await this.updateJobAllocation(sourceAllocationId, {
          quantity: sourceQty - movedQty,
        });
      }
      if (!sourceOk) {
        return { ok: false, error: "Could not update source allocation." };
      }

      // Record outbound re-allocation on source job
      if (orgId && sourceJobId) {
        void writeReallocationAuditEntry(
          sourceJobId, orgId, "out", destJobLabel, sku, productName, movedQty,
        );
      }

      const dest = await this.createJobAllocation({
        jobId: destJobId,
        jobLabel: destJobLabel,
        sku,
        productName,
        quantity: movedQty,
        status: "Reserved",
        allocatedBy: "reallocation-override",
      });
      if (!dest) {
        return {
          ok: false,
          error: "Source updated but destination could not be created — please allocate manually.",
        };
      }

      // Sync destination job readiness + record inbound re-allocation
      await syncJobMaterialReadiness(destJobId);
      if (orgId) {
        void writeReallocationAuditEntry(
          destJobId, orgId, "in", sourceJobLabel ?? sourceJobId ?? "another job",
          sku, productName, movedQty,
        );
      }

      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  },
};
